/**
 * Auth Error Event Emitter
 * Lightweight event system for auth-related errors (no external dependencies)
 */

// Event types
export type AuthErrorType = 'auth:unauthorized' | 'auth:forbidden' | 'auth:deleted';

// Event payload
export interface AuthErrorPayload {
  type: AuthErrorType;
  statusCode: number;
  message: string;
  url?: string;
}

// Event callback type
export type AuthErrorCallback = (payload: AuthErrorPayload) => void;

// Internal listener storage
const listeners: Set<AuthErrorCallback> = new Set();

/**
 * Emit an auth error event
 * @param type - The auth error type
 * @param details - Additional error details (statusCode, message, url)
 */
export function emitAuthError(
  type: AuthErrorType,
  details?: Partial<Omit<AuthErrorPayload, 'type'>>
): void {
  const statusCode = details?.statusCode ?? getDefaultStatusCode(type);
  const message = details?.message ?? getDefaultMessage(type);

  const payload: AuthErrorPayload = {
    type,
    statusCode,
    message,
    url: details?.url,
  };

  // Notify all listeners
  listeners.forEach(callback => {
    try {
      callback(payload);
    } catch (error) {
      console.error('Error in auth error listener:', error);
    }
  });
}

/**
 * Subscribe to auth error events
 * @param callback - Function to call when auth errors occur
 * @returns Unsubscribe function
 */
export function onAuthError(callback: AuthErrorCallback): () => void {
  listeners.add(callback);

  // Return unsubscribe function
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Unsubscribe from auth error events
 * @param callback - The callback to remove
 */
export function offAuthError(callback: AuthErrorCallback): void {
  listeners.delete(callback);
}

/**
 * Clear all listeners (useful for testing)
 */
export function clearAuthErrorListeners(): void {
  listeners.clear();
}

/**
 * Get default status code for error type
 */
function getDefaultStatusCode(type: AuthErrorType): number {
  switch (type) {
    case 'auth:unauthorized':
      return 401;
    case 'auth:forbidden':
      return 403;
    case 'auth:deleted':
      return 404;
    default:
      return 500;
  }
}

/**
 * Get default message for error type
 */
function getDefaultMessage(type: AuthErrorType): string {
  switch (type) {
    case 'auth:unauthorized':
      return 'Unauthorized - please log in again';
    case 'auth:forbidden':
      return 'Access forbidden';
    case 'auth:deleted':
      return 'Account or profile not found';
    default:
      return 'Authentication error';
  }
}
